<template>
  <form @submit.prevent="submit">
    <div class="row">
      <div class="col-md-12">
        <FormSelect
          name="department"
          v-model="fields.department"
          :label="`${$t('global.department')} *`"
          :errors="validation.fields.department.errors"
          :dirty="validation.fields.department.dirty"
        >
          <option value="" disabled>{{ $t("global.none-selected") }}</option>
          <option
            v-for="(department, index) in ticketOptions.departments"
            :key="index"
            :value="department.id"
          >
            {{ department.identity }}
          </option>
        </FormSelect>
      </div>
      <div class="col-md-12">
        <FormInput
          name="subject"
          v-model="fields.subject"
          :label="`${$t('global.subject')} *`"
          :errors="validation.fields.subject.errors"
          :dirty="validation.fields.subject.dirty"
          :disabled="loading"
          class="pb-2"
        />
      </div>
      <div class="col-md-12">
        <FormSelect
          name="service"
          v-model="fields.service"
          :label="`${$t('global.service')}`"
          :errors="validation.fields.service.errors"
          :dirty="validation.fields.service.dirty"
        >
          <option value="" disabled>{{ $t("global.none-selected") }}</option>
          <option
            v-for="(service, index) in ticketOptions.service"
            :key="index"
            :value="service.id"
          >
            {{ service.identity }}
          </option>
        </FormSelect>
      </div>
      <div class="col-md-12">
        <FormCustomListInput
          name="recipients"
          type="email"
          v-model="fields.recipients"
          :label="`${$t('global.recipients')}`"
        />
      </div>
      <div class="col-md-12">
        <FormTextArea
          name="reply"
          v-model="fields.reply"
          :label="`${$t('global.reply')} *`"
          :errors="validation.fields.reply.errors"
          :dirty="validation.fields.reply.dirty"
          :disabled="loading"
          class="pb-2"
        />
      </div>
      <div class="col-md-6">
        <FormSelect name="priority" v-model="fields.priority" :label="`${$t('global.priority')}`">
          <option
            v-for="(priority, index) in ticketOptions.priority"
            :key="index"
            :value="priority.id"
            :selected="true"
          >
            {{ priority.identity }}
          </option>
        </FormSelect>
      </div>
      <div class="col-md-6">
        <FormSelect name="category" v-model="fields.category" :label="`${$t('global.category')}`">
          <option value="" disabled>{{ $t("global.none-selected") }}</option>
          <option
            v-for="(category, index) in ticketOptions.category"
            :key="index"
            :value="category.id"
          >
            {{ category.identity }}
          </option>
        </FormSelect>
      </div>
      <div v-if="displayAdditionalFields" class="col-md-6">
        <FormInput
          name="phone"
          v-model="fields.phone"
          :label="`${$t('global.phone')}`"
          :errors="validation.fields.phone.errors"
          :dirty="validation.fields.phone.dirty"
          :disabled="loading"
          class="pb-2"
        />
      </div>
      <div v-if="displayAdditionalFields" class="col-md-6">
        <FormInput
          name="domain"
          v-model="fields.domain"
          :label="`${$t('global.domain')}`"
          :errors="validation.fields.domain.errors"
          :dirty="validation.fields.domain.dirty"
          :disabled="loading"
          class="pb-2"
        />
      </div>
      <div class="col-md-12">
        <Dropzone
          v-model="ticketFileIds"
          @sending="uploadingTicketFiles"
          @complete="completedTicketFileUpload"
        >
          <div class="mt-4 p-4 ticket-files">
            <span class="mr-2 my-2 text-secondary">{{ $t("global.attach-ticket-files") }}:</span>
            <Btn id="dropzone-upload-file" class="mr-2 my-2" :loading="ticketFilesLoading">
              {{ $t("global.browse-files") }}
            </Btn>
            <div class="dropzone-previews"></div>
          </div>
        </Dropzone>
      </div>
    </div>
    <div class="form-buttons">
      <Btn class="mr-2" variant="light" type="button" :disabled="loading" @click="cancel">
        {{ $t("global.cancel") }}
      </Btn>
      <Btn variant="primary" type="submit" :disabled="!isSubmittable()" :loading="loading">
        {{ $t("global.add") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { computed, reactive, ref, watch } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import constants from "@/utils/constants";
import Dropzone from "@/components/display/Dropzone";
import FormCustomListInput from "@/components/display/form/FormCustomListInput";
import FormInput from "@/components/display/form/FormInput";
import FormSelect from "@/components/display/form/FormSelect";
import FormTextArea from "@/components/display/form/FormTextArea";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { addTicket as validationRules } from "leatherman-js/utils/validations/forms/ticket";

export default {
  name: "ContactSupportForm",
  components: {
    Dropzone,
    FormCustomListInput,
    FormInput,
    FormSelect,
    FormTextArea,
    Btn,
  },
  props: {
    departmentId: Number,
    reply: String,
    serviceId: Number,
    subject: String,
    support: Boolean,
    ticketOptions: Object,
  },
  setup(props, { emit, root }) {
    const loading = ref(false);
    const errors = ref(null);
    const fields = reactive({
      department: props.departmentId,
      subject: props.subject,
      service: props.serviceId,
      recipients: [],
      reply: props.reply,
      priority: constants.TICKETS.DEFAULT_PRIORITY_ID,
      category: null,
      attachments: [],
      phone: null,
      domain: null,
    });
    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      {
        department: {
          required: root.$t("validation.department-required"),
        },
        subject: {
          required: root.$t("validation.subject-required"),
        },
        reply: {
          required: root.$t("validation.reply-required"),
        },
        phone: {
          phone: root.$t("validation.invalid-phone"),
        },
        domain: {
          domain: root.$t("validation.domain-invalid"),
        },
      }
    );

    const ticketFileIds = ref([]);
    const ticketFilesLoading = ref(false);
    const uploadingTicketFiles = () => (ticketFilesLoading.value = true);
    const completedTicketFileUpload = () => (ticketFilesLoading.value = false);
    const displayAdditionalFields = computed(
      () => fields.department === constants.TICKETS.DEPARTMENT_ID_WITH_ADDITIONAL_FIELDS
    );

    const cancel = () => emit("close");
    const isSubmittable = () =>
      !loading.value &&
      validation.errors.length === 0 &&
      (validation.fields.department.dirty ||
        validation.fields.subject.dirty ||
        validation.fields.service.dirty ||
        validation.fields.recipients.dirty ||
        validation.fields.reply.dirty ||
        validation.fields.priority.dirty ||
        validation.fields.category.dirty);

    watch(fields, (sel) => {
      if (
        process.env.VUE_APP_SUPPORT_MIGRATION_DEPARTMENT &&
        sel.department == process.env.VUE_APP_SUPPORT_MIGRATION_DEPARTMENT
      ) {
        root.$router.push({ name: "request-migration" }).catch(() => {});
        cancel();
      }
    });

    const submit = () => {
      errors.value = null;
      const name = fields.subject;

      validation
        .validate()
        .then(() => {
          fields.attachments = ticketFileIds.value;
          loading.value = true;
          store
            .dispatch("tickets/create", fields)
            .then((ticket) => {
              store.dispatch("toasts/addToast", {
                variant: "info",
                message: root.$t("toasts.ticket-adding-message", { name: name }),
              });
              emit("close");

              root.$router.push({ name: "ticket-detail", params: { id: ticket.id } });
            })
            .catch((e) => {
              const errors = e.request.response;
              for (let key in errors) {
                let message = errors[key];
                if (message.length > 0) {
                  store.dispatch("toasts/addToast", {
                    variant: "error",
                    message: root.$t("toasts.ticket-adding-error-message", { name: message }),
                  });
                } else {
                  store.dispatch("toasts/addToast", {
                    variant: "error",
                    message: root.$t("toasts.ticket-adding-error-message", {
                      name: name,
                    }),
                  });
                }
              }
            })
            .finally(() => {
              loading.value = false;
            });
        })
        .catch(() => {});
    };
    return {
      errors,
      fields,
      loading,
      submit,
      ticketFileIds,
      ticketFilesLoading,
      uploadingTicketFiles,
      completedTicketFileUpload,
      validation,
      cancel,
      isSubmittable,
      displayAdditionalFields,
    };
  },
};
</script>

<style lang="scss" scoped></style>
